// src/pages/common-components/Loan-Calculator/LoanCalculatorModal.tsx
// Lets a customer estimate a monthly installment for a car using price,
// interest rate, loan term and down payment. Purely a client-side estimate —
// nothing is submitted or stored, so there's no service call here.
import React, { useMemo, useState, useEffect } from 'react';
import { useCompany } from '../../Contexts/CompanyContext';

interface LoanCalculatorModalProps {
  carName?: string;
  carPrice?: number;
  onClose: () => void;
  // When true, renders inline in the page flow instead of as an overlay dialog.
  inline?: boolean;
}

const styles = `
  .lcm-overlay {
    position: fixed; inset: 0; z-index: 9999; background: rgba(10,8,6,0.5);
    animation: lcm-fade-in 0.25s ease;
  }
  .lcm-dialog {
    background: #fff; width: 100%; max-width: 420px; position: fixed;
    top: 0; left: 0; height: 100%; height: 100dvh;
    display: flex; flex-direction: column; box-shadow: 8px 0 24px rgba(0,0,0,0.15);
    animation: lcm-slide-in 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .lcm-dialog--inline {
    max-width: 520px; position: static; height: auto; box-shadow: none;
    background: transparent; animation: none;
  }
  @keyframes lcm-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes lcm-slide-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
  @media (max-width: 480px) {
    .lcm-dialog { max-width: 100%; }
  }

  .lcm-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px; border-bottom: 1px solid #eee; flex-shrink: 0;
  }
  .lcm-dialog--inline .lcm-header { padding: 0 0 18px; border-bottom: none; }
  .lcm-body {
    padding: 20px; overflow-y: auto; flex: 1 1 auto;
  }
  .lcm-dialog--inline .lcm-body { padding: 0; overflow-y: visible; }
  .lcm-footer {
    padding: 16px 20px; border-top: 1px solid #eee; flex-shrink: 0;
  }
  .lcm-footer-close {
    width: 100%; height: 44px; padding: 0 13px; background: transparent; color: #717fe0;
    border: 2px solid #717fe0; border-radius: 100px; font-size: 13px; font-weight: 600;
    letter-spacing: 0.5px; text-transform: uppercase; cursor: pointer; transition: all 0.25s ease;
  }
  .lcm-footer-close:hover { background: #717fe0; color: #fff; }
  .lcm-close {
    background: #f0f0f5; border: none; border-radius: 50%; width: 38px; height: 38px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; cursor: pointer; color: #6b7280; line-height: 1; flex-shrink: 0;
    transition: all 0.25s ease;
  }
  .lcm-close:hover { background: #717fe0; color: #fff; transform: rotate(90deg); }
  .lcm-title { font-size: 18px; font-weight: 600; margin: 0; }
  .lcm-subtitle { font-size: 14px; color: #777; margin-bottom: 18px; }
  .lcm-field { margin-bottom: 14px; }
  .lcm-field label { display: block; font-size: 13px; margin-bottom: 5px; color: #444; }
  .lcm-field input {
    width: 100%; padding: 10px 12px; border: 1px solid #ddd; border-radius: 3px;
    font-size: 14px; font-family: inherit; box-sizing: border-box;
  }
  .lcm-row { display: flex; gap: 12px; }
  .lcm-row .lcm-field { flex: 1; }
  .lcm-result {
    margin-top: 8px; padding: 18px; background: #f7f5f2; border-radius: 4px;
    text-align: center;
  }
  .lcm-result-label { font-size: 12px; color: #777; text-transform: uppercase; letter-spacing: 1px; }
  .lcm-result-value { font-size: 26px; font-weight: 700; color: #222; margin: 4px 0; }
  .lcm-breakdown { margin-top: 12px; font-size: 13px; color: #555; }
  .lcm-breakdown-row {
    display: flex; justify-content: space-between; padding: 4px 0;
    border-bottom: 1px solid #eee;
  }
  .lcm-breakdown-row:last-child { border-bottom: none; }
  .lcm-error { color: #c0392b; font-size: 13px; margin-bottom: 10px; }
  .lcm-disclaimer { font-size: 11px; color: #999; margin-top: 14px; line-height: 1.5; }
`;

const LoanCalculatorModal: React.FC<LoanCalculatorModalProps> = ({ carName, carPrice, onClose, inline = false }) => {
  const { formatPrice } = useCompany();

  const [price, setPrice] = useState(carPrice ? String(carPrice) : '');
  const [interestRate, setInterestRate] = useState('10');
  const [loanTermMonths, setLoanTermMonths] = useState('60');
  const [downPayment, setDownPayment] = useState('');

  // Lock body scroll while the drawer overlay is open (not needed for the
  // inline rendering, which has no overlay).
  useEffect(() => {
    if (inline) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarWidth + 'px';
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [inline]);

  const numericPrice = parseFloat(price) || 0;
  const numericRate = parseFloat(interestRate) || 0;
  const numericTermMonths = parseInt(loanTermMonths, 10) || 0;
  const numericDownPayment = parseFloat(downPayment) || 0;

  const error =
    numericPrice <= 0 ? 'Enter the car price to calculate an installment.'
    : numericDownPayment >= numericPrice ? 'Down payment must be less than the car price.'
    : numericTermMonths <= 0 ? 'Enter a loan term in months.'
    : '';

  const result = useMemo(() => {
    if (error) return null;
    const principal = numericPrice - numericDownPayment;
    const monthlyRate = numericRate / 100 / 12;
    const monthlyPayment = monthlyRate === 0
      ? principal / numericTermMonths
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, numericTermMonths)) /
        (Math.pow(1 + monthlyRate, numericTermMonths) - 1);
    const totalPayment = monthlyPayment * numericTermMonths;
    const totalInterest = totalPayment - principal;
    return { principal, monthlyPayment, totalPayment, totalInterest };
  }, [error, numericPrice, numericDownPayment, numericRate, numericTermMonths]);

  const dialogContent = (
    <div
      className={`lcm-dialog${inline ? ' lcm-dialog--inline' : ''}`}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : true}
    >
      <div className="lcm-header">
        <h3 className="lcm-title">Loan Calculator</h3>
        {!inline && (
          <button className="lcm-close" onClick={onClose} aria-label="Close">&times;</button>
        )}
      </div>

      <div className="lcm-body">
        <p className="lcm-subtitle">
          {carName ? <>Estimate your monthly installment for <strong>{carName}</strong>.</> : 'Estimate your monthly installment.'}
          {' '}This is an estimate only — actual terms depend on the lender.
        </p>

        {error && <div className="lcm-error">{error}</div>}

        <div className="lcm-field">
          <label>Price</label>
          <input
            type="number" min="0" value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g. 5000000"
          />
        </div>

        <div className="lcm-row">
          <div className="lcm-field">
            <label>Interest Rate (% per year)</label>
            <input
              type="number" min="0" step="0.1" value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              placeholder="e.g. 10"
            />
          </div>
          <div className="lcm-field">
            <label>Loan Term (months)</label>
            <input
              type="number" min="1" step="1" value={loanTermMonths}
              onChange={(e) => setLoanTermMonths(e.target.value)}
              placeholder="e.g. 60"
            />
          </div>
        </div>

        <div className="lcm-field">
          <label>Down Payment</label>
          <input
            type="number" min="0" value={downPayment}
            onChange={(e) => setDownPayment(e.target.value)}
            placeholder="e.g. 1000000"
          />
        </div>

        {result && (
          <div className="lcm-result">
            <div className="lcm-result-label">Estimated Monthly Payment</div>
            <div className="lcm-result-value">{formatPrice(result.monthlyPayment)}</div>
            <div className="lcm-breakdown">
              <div className="lcm-breakdown-row">
                <span>Loan Amount</span>
                <span>{formatPrice(result.principal)}</span>
              </div>
              <div className="lcm-breakdown-row">
                <span>Total Interest</span>
                <span>{formatPrice(result.totalInterest)}</span>
              </div>
              <div className="lcm-breakdown-row">
                <span>Total Payable</span>
                <span>{formatPrice(result.totalPayment)}</span>
              </div>
            </div>
          </div>
        )}

        <p className="lcm-disclaimer">
          This calculator provides an estimate for planning purposes only and does not constitute a loan offer.
          Speak to our sales team or your bank for exact financing terms.
        </p>
      </div>

      {!inline && (
        <div className="lcm-footer">
          <button className="lcm-footer-close" onClick={onClose}>Close</button>
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <>
        <style>{styles}</style>
        {dialogContent}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="lcm-overlay" onClick={onClose}>
        {dialogContent}
      </div>
    </>
  );
};

export default LoanCalculatorModal;
